import { useState, useEffect } from "react";
import { Trash2, Download } from "lucide-react";
import UploadProjectOutputModal from "./UploadProjectOutputModal";
import api from "../../../../../utils/axiosInstance";
import { toast, confirmDialog } from "../../../../../components/Toast/toast";

const ProjectOutputFilesCard = ({ projectId, files = [], onRefresh }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [jobDetailsMap, setJobDetailsMap] = useState({});

  // Fetch job details for all unique job_ids in files
  useEffect(() => {
    const uniqueJobIds = [
      ...new Set(files.map((f) => f.job_id).filter(Boolean)),
    ];
    if (uniqueJobIds.length === 0) return;

    const fetchJobDetails = async () => {
      try {
        const results = await Promise.all(
          uniqueJobIds.map((jobId) =>
            api
              .get(`/jobs/${jobId}`, { withCredentials: true })
              .then((res) => ({ jobId, data: res.data.data }))
              .catch(() => ({ jobId, data: null })),
          ),
        );

        const map = {};
        results.forEach(({ jobId, data }) => {
          if (data) map[jobId] = data;
        });
        setJobDetailsMap(map);
      } catch (error) {
        console.error("Failed to fetch job details:", error);
      }
    };

    fetchJobDetails();
  }, [files]);

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDownloadSingle = async (file) => {
    try {
      const response = await api.get(
        `/download-file?path=${encodeURIComponent(file.file_path)}`,
        { responseType: "blob" },
      );
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_file_name || "file";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  const handleDownloadZip = async () => {
    if (files.length === 0) return toast.error("No files to download");
    try {
      setDownloading(true);
      const response = await api.get(
        `/project-output-files/download-zip?project_id=${projectId}`,
        { responseType: "blob" },
      );
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `project-${projectId}-output-files.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download zip error:", error);
      toast.error("Failed to download files as zip");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async (file) => {
    if (
      !await confirmDialog(
        `Are you sure you want to remove "${file.original_file_name}" from project output?`,
      )
    )
      return;
    try {
      await api.delete(`/project-output-files/${file.id}`);
      onRefresh();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.message || "Failed to delete file");
    }
  };

  const getJobField = (jobId, field) => {
    const job = jobDetailsMap[jobId];
    if (!job) return "—";

    switch (field) {
      case "service":
        return typeof job.service === "object"
          ? job.service?.name || "—"
          : job.service_name || job.service || "—";

      case "lang_pair":
        return job.languagePair?.sourceLanguage?.name &&
          job.languagePair?.targetLanguage?.name
          ? `${job.languagePair.sourceLanguage.name} → ${job.languagePair.targetLanguage.name}`
          : "—";

      case "vendor":
        if (!job.vendor) return "—";
        if (job.vendor.type === "Company") {
          return job.vendor.company_name || "—";
        }
        const firstName = job.vendor.primary_users?.first_name || "";
        const lastName = job.vendor.primary_users?.last_name || "";
        return `${firstName} ${lastName}`.trim() || "—";
      default:
        return "—";
    }
  };

  const HEADERS = [
    "File #",
    "Filename",
    "Size",
    "Uploaded at",
    "Uploaded by",
    "Job Code",
    "Job Service",
    "Job Language Pair",
    "Job Vendor",
  ];

  return (
    <>
      <section>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
          <h2 className="font-bold text-gray-900">
            Project Output Files <span className="text-gray-500 font-medium">({files.length})</span>
          </h2>
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
            >
              Upload
            </button>
            <button
              onClick={() => toast.error("Add URL feature coming soon!")}
              className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded"
            >
              Add URL
            </button>
            <button
              onClick={handleDownloadZip}
              disabled={downloading || files.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
            >
              <Download className="w-4 h-4" />
              {downloading ? "Downloading..." : "Download as ZIP"}
            </button>
          </div>
        </div>

        <div className="w-full bg-white shadow rounded-lg overflow-hidden">
          <div className="max-h-[300px] overflow-y-auto overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                  {HEADERS.map((h) => (
                    <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {files.length === 0 ? (
                  <tr>
                    <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                      No project output files found.
                    </td>
                  </tr>
                ) : (
                  files.map((file, index) => {
                    const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                    return (
                      <tr key={file.id || index} className={`${rowBg} hover:bg-blue-50 transition-colors`}>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          {file.file_code || `#${index + 1}`}
                        </td>
                        <td className="px-3 py-2 text-xs text-center break-words">
                          <button
                            onClick={() => handleDownloadSingle(file)}
                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            {file.original_file_name || "—"}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          {formatFileSize(file.file_size)}
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-500">
                          {formatDateTime(file.uploaded_at)}
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          {file.uploader?.details
                            ? `${file.uploader.details.first_name} ${file.uploader.details.last_name}`
                            : file.uploader?.email || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          —
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          {file.job_id ? getJobField(file.job_id, "service") : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          {file.job_id ? getJobField(file.job_id, "lang_pair") : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          {file.job_id ? getJobField(file.job_id, "vendor") : "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showUploadModal && (
        <UploadProjectOutputModal
          projectId={projectId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default ProjectOutputFilesCard;